import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  DEFAULT_TOAST_APPEARANCE,
  resolveToastAppearance,
  TOAST_APPEARANCE_PLATFORM_SETTING_KEY,
  type ToastAppearance,
} from '@/lib/toastAppearance';
import {
  hasPendingSsoCallbackAdmission,
  subscribeSsoCallbackAdmission,
} from '@/lib/ssoCallbackAdmission';

// Scoped to auth state: `<Toaster />` mounts before routing, so the query runs first
// as an anonymous user (RLS returns no row → default appearance). Keying by userId keeps
// that anonymous default from being served to the signed-in session, where the real
// super-admin-selected appearance must be fetched. Mirrors useFeatureFlags' cache scoping.
export const toastAppearanceQueryKey = (userId?: string) =>
  ['platform-settings', TOAST_APPEARANCE_PLATFORM_SETTING_KEY, userId ?? 'anonymous'] as const;

const fetchToastAppearance = async (): Promise<ToastAppearance> => {
  const { data, error } = await supabase
    .from('platform_settings')
    .select('value')
    .eq('key', TOAST_APPEARANCE_PLATFORM_SETTING_KEY)
    .maybeSingle();

  if (error) {
    // Non-fatal: unauthenticated users and unset config fall back to the default finish.
    console.error('Failed to load toast appearance setting:', error);
    return { ...DEFAULT_TOAST_APPEARANCE };
  }

  return resolveToastAppearance(data?.value);
};

export const useToastAppearance = (userId?: string) => {
  // ENT-4771: `<Toaster />` mounts at the root, and the read goes through the
  // global client — which, during a fresh SAML callback, already holds a bearer
  // that ENT-4508 rejects (28000 session_revoked) until sso-claim-membership
  // admits it. Hold the read (default appearance meanwhile) until then.
  const ssoCallbackAdmissionPending = useSyncExternalStore(
    subscribeSsoCallbackAdmission,
    hasPendingSsoCallbackAdmission,
    () => false,
  );
  const query = useQuery({
    queryKey: toastAppearanceQueryKey(userId),
    queryFn: fetchToastAppearance,
    staleTime: 5 * 60 * 1000,
    enabled: !ssoCallbackAdmissionPending,
  });

  return {
    ...query,
    appearance: query.data ?? DEFAULT_TOAST_APPEARANCE,
  };
};
